import { authService } from './authService'

const LOCK_KEY = 'batipilot.sessionLocked'
const LOCK_REDIRECT_KEY = 'batipilot.lockRedirect'
const LAST_ACTIVITY_KEY = 'batipilot.lastActivity'

const DEFAULT_TIMEOUT_MINUTES = 60
const MIN_TIMEOUT_MINUTES = 15
const MAX_TIMEOUT_MINUTES = 480
const ACTIVITY_DEBOUNCE_MS = 800
const FALLBACK_CHECK_MS = 30000

const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'pointerdown']

let timeoutMinutes = DEFAULT_TIMEOUT_MINUTES
let lockTimer = null
let fallbackInterval = null
let activityCleanup = null
let storageCleanup = null
let routerRef = null
let activityDebounceTimer = null

const readStorage = (key) => localStorage.getItem(key) ?? sessionStorage.getItem(key)

const writeStorage = (key, value) => {
  if (value == null) {
    localStorage.removeItem(key)
    sessionStorage.removeItem(key)
    return
  }
  localStorage.setItem(key, value)
  sessionStorage.setItem(key, value)
}

const removeStorage = (key) => {
  localStorage.removeItem(key)
  sessionStorage.removeItem(key)
}

const clampTimeoutMinutes = (minutes) => {
  const value = Number(minutes)
  if (!Number.isFinite(value)) {
    return DEFAULT_TIMEOUT_MINUTES
  }
  return Math.min(MAX_TIMEOUT_MINUTES, Math.max(MIN_TIMEOUT_MINUTES, Math.round(value)))
}

const getTimeoutMs = () => timeoutMinutes * 60 * 1000

const getLastActivity = () => {
  const raw = readStorage(LAST_ACTIVITY_KEY)
  const value = Number(raw)
  return Number.isFinite(value) && value > 0 ? value : 0
}

export const sessionLockService = {
  configure(settings = {}) {
    timeoutMinutes = clampTimeoutMinutes(settings?.sessionTimeoutMinutes)
    if (routerRef && authService.isAuthenticated()) {
      this.scheduleLock()
    }
  },

  getTimeoutMinutes() {
    return timeoutMinutes
  },

  getTimeoutMs() {
    return getTimeoutMs()
  },

  touchActivity() {
    if (!authService.isAuthenticated() || this.isLocked()) {
      return
    }
    writeStorage(LAST_ACTIVITY_KEY, String(Date.now()))
    this.scheduleLock()
  },

  isLocked() {
    return readStorage(LOCK_KEY) === 'true'
  },

  lock(redirectPath = '/dashboard') {
    writeStorage(LOCK_KEY, 'true')
    if (redirectPath) {
      writeStorage(LOCK_REDIRECT_KEY, redirectPath)
    }
  },

  unlockSession() {
    removeStorage(LOCK_KEY)
    this.touchActivity()
  },

  getLockRedirect() {
    return readStorage(LOCK_REDIRECT_KEY) || '/dashboard'
  },

  clearLockRedirect() {
    removeStorage(LOCK_REDIRECT_KEY)
  },

  getRemainingMs() {
    const lastActivity = getLastActivity()
    if (!lastActivity) {
      return getTimeoutMs()
    }
    return Math.max(0, getTimeoutMs() - (Date.now() - lastActivity))
  },

  shouldLock() {
    if (!authService.isAuthenticated() || this.isLocked()) {
      return false
    }

    const lastActivity = getLastActivity()
    if (!lastActivity) {
      return false
    }

    return Date.now() - lastActivity >= getTimeoutMs()
  },

  applyLock() {
    if (!authService.isAuthenticated() || this.isLocked() || !routerRef) {
      return
    }

    if (routerRef.currentRoute.value.name === 'auth.lock-screen') {
      return
    }

    const currentPath = routerRef.currentRoute.value.fullPath
    this.lock(currentPath)
    routerRef.push({
      name: 'auth.lock-screen',
      query: { redirect: currentPath }
    })
  },

  scheduleLock() {
    if (lockTimer) {
      clearTimeout(lockTimer)
      lockTimer = null
    }

    if (!authService.isAuthenticated() || this.isLocked()) {
      return
    }

    const remainingMs = this.getRemainingMs()
    if (remainingMs <= 0) {
      this.applyLock()
      return
    }

    lockTimer = setTimeout(() => {
      if (this.shouldLock()) {
        this.applyLock()
      }
    }, remainingMs)
  },

  onUserActivity() {
    if (activityDebounceTimer) {
      clearTimeout(activityDebounceTimer)
    }
    activityDebounceTimer = setTimeout(() => {
      this.touchActivity()
    }, ACTIVITY_DEBOUNCE_MS)
  },

  startMonitoring(router) {
    routerRef = router
    this.stopMonitoring()

    if (!getLastActivity()) {
      this.touchActivity()
    } else {
      this.scheduleLock()
    }

    const onActivity = () => this.onUserActivity()
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true })
    })
    activityCleanup = () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity)
      })
    }

    const onStorage = (event) => {
      if (event.key === LOCK_KEY && event.newValue === 'true' && routerRef) {
        if (routerRef.currentRoute.value.name !== 'auth.lock-screen') {
          routerRef.push({
            name: 'auth.lock-screen',
            query: { redirect: routerRef.currentRoute.value.fullPath }
          })
        }
      }
      if (event.key === LAST_ACTIVITY_KEY && event.newValue) {
        this.scheduleLock()
      }
    }
    window.addEventListener('storage', onStorage)
    storageCleanup = () => window.removeEventListener('storage', onStorage)

    fallbackInterval = setInterval(() => {
      if (this.shouldLock()) {
        this.applyLock()
      }
    }, FALLBACK_CHECK_MS)
  },

  stopMonitoring() {
    if (lockTimer) {
      clearTimeout(lockTimer)
      lockTimer = null
    }
    if (fallbackInterval) {
      clearInterval(fallbackInterval)
      fallbackInterval = null
    }
    if (activityDebounceTimer) {
      clearTimeout(activityDebounceTimer)
      activityDebounceTimer = null
    }
    if (activityCleanup) {
      activityCleanup()
      activityCleanup = null
    }
    if (storageCleanup) {
      storageCleanup()
      storageCleanup = null
    }
  },

  resetAfterLogin() {
    removeStorage(LOCK_KEY)
    removeStorage(LOCK_REDIRECT_KEY)
    writeStorage(LAST_ACTIVITY_KEY, String(Date.now()))
  },

  clear() {
    removeStorage(LOCK_KEY)
    removeStorage(LOCK_REDIRECT_KEY)
    removeStorage(LAST_ACTIVITY_KEY)
    this.stopMonitoring()
  },

  async bootstrap(router) {
    if (!authService.isAuthenticated()) {
      this.clear()
      return
    }

    if (!getLastActivity()) {
      this.touchActivity()
    } else if (this.shouldLock()) {
      this.lock(router.currentRoute.value?.fullPath || '/dashboard')
      if (router.currentRoute.value?.name !== 'auth.lock-screen') {
        await router.push({
          name: 'auth.lock-screen',
          query: { redirect: router.currentRoute.value?.fullPath || '/dashboard' }
        })
      }
      return
    }

    this.startMonitoring(router)
  }
}
